/**
 * React context for the active tenant theme.
 *
 * Components that need branding info (product name, logo, badge, footer)
 * read from this context instead of hardcoding. The provider loads the
 * theme on mount and applies CSS custom properties.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import {
  DEFAULT_THEME,
  applyTheme,
  loadTheme,
  type TenantTheme,
} from "@/lib/theme";

const ThemeContext = createContext<TenantTheme>(DEFAULT_THEME);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme] = useState<TenantTheme>(() => {
    const t = loadTheme();
    applyTheme(t);
    return t;
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): TenantTheme {
  return useContext(ThemeContext);
}
